/**
 * Return Transaction Workflow Form Module
 */

import { els } from '../ui/elements.js';
import { getState, addTransaction, updateInventoryItem } from '../state/store.js';
import { openModal, closeModal } from '../ui/modals.js';
import { showCustomAlert, escapeHtml } from '../ui/alerts.js';
import { syncAfterLocalChange } from '../services/sync.js';
import { getAuthenticatedOperatorEmail, getAuthenticatedOperatorName } from '../services/auth.js';
import { rpcReturnInventory } from '../services/rpc.js';
import { hasCloudSyncConfig } from '../services/supabase.js';

let returnInProgress = false;

/**
 * Calculates remaining unreturned quantity for a borrow transaction.
 * @param {import('../types/index.js').Transaction} borrowTx 
 * @returns {number}
 */
export function getBorrowUnreturnedQty(borrowTx) {
  if (!borrowTx || borrowTx.type !== 'borrow') return 0;
  const { transactions } = getState();

  const totalQty = Number(borrowTx.quantity) || 0;
  const relatedReturns = transactions.filter(t => 
    !t.deleted && 
    t.type === 'return' && 
    (t.notes?.includes(`[ยืม: ${borrowTx.id}]`) || t.borrowTxId === borrowTx.id || (t.itemId === borrowTx.itemId && t.timestamp > borrowTx.timestamp))
  );

  const returnedAndConsumed = relatedReturns.reduce((sum, r) => sum + (Number(r.quantity) || 0) + (Number(r.consumedQty) || 0), 0);
  return Math.max(0, Number((totalQty - returnedAndConsumed).toFixed(5)));
}

/**
 * Populates borrow sessions with unreturned items into the return dropdown.
 */
export function populateReturnDropdown() {
  const selectEl = els.selReturnBorrowTx || document.getElementById('selReturnBorrowTx');
  if (!selectEl) return;
  selectEl.innerHTML = '<option value="">-- เลือกรายการยืมที่ต้องการคืน --</option>';

  const { transactions } = getState();
  const borrows = transactions.filter(t => !t.deleted && t.type === 'borrow');
  const sessionsMap = new Map();

  borrows.forEach(bt => {
    const unreturnedQty = getBorrowUnreturnedQty(bt);
    if (unreturnedQty > 0) {
      if (!sessionsMap.has(bt.timestamp)) {
        sessionsMap.set(bt.timestamp, {
          timestamp: bt.timestamp,
          purpose: bt.notes || 'ไม่ระบุวัตถุประสงค์',
          borrower: bt.borrowerName || bt.borrower_name || bt.operatorEmail || '',
          count: 0
        });
      }
      sessionsMap.get(bt.timestamp).count++;
    }
  });

  const sortedSessions = Array.from(sessionsMap.values()).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  sortedSessions.forEach(session => {
    const opt = document.createElement('option');
    opt.value = session.timestamp;

    let dateStr = session.timestamp;
    try {
      dateStr = new Date(session.timestamp).toLocaleString('th-TH', { hour12: false });
    } catch (_) {}

    const borrowerSuffix = session.borrower ? ` | ผู้ยืม: ${session.borrower}` : '';
    opt.textContent = `${dateStr}${borrowerSuffix} | วัตถุประสงค์: ${session.purpose} (${session.count} รายการค้างคืน)`;
    selectEl.appendChild(opt);
  });

  if (sortedSessions.length === 0) {
    const opt = document.createElement('option');
    opt.value = '';
    opt.textContent = 'ไม่มีรายการสินค้าค้างคืนในระบบ';
    selectEl.appendChild(opt);
  }
}

/**
 * Renders unreturned item rows for a selected borrow timestamp in a spacious responsive table.
 * @param {string} timestamp 
 */
export function renderReturnItemsForSession(timestamp) {
  const section = els.divReturnItemsSection || document.getElementById('divReturnItemsSection');
  const container = els.divReturnItemsContainer || document.getElementById('divReturnItemsContainer');
  if (!container) return;
  container.innerHTML = '';

  if (!timestamp) {
    if (section) section.style.display = 'none';
    return;
  }

  const { transactions, inventory } = getState();
  const sessionBorrows = transactions.filter(t => !t.deleted && t.type === 'borrow' && t.timestamp === timestamp);
  const activeBorrows = sessionBorrows.filter(bt => getBorrowUnreturnedQty(bt) > 0);

  if (activeBorrows.length === 0) {
    if (section) section.style.display = 'none';
    showCustomAlert('ไม่มีรายการสินค้าค้างคืนในใบยืมนี้ (ส่งคืนครบหมดแล้ว)');
    return;
  }

  if (section) section.style.display = 'flex';

  const tableWrapper = document.createElement('div');
  tableWrapper.className = 'return-table-wrapper';
  tableWrapper.style.cssText = 'overflow-x: auto; -webkit-overflow-scrolling: touch; border: 1px solid var(--panel-border); border-radius: 10px; background: var(--card-bg, #ffffff); width: 100%;';

  let tableHtml = `
    <table class="return-table" style="width: 100%; min-width: 720px; border-collapse: collapse; font-size: 0.875rem;">
      <thead>
        <tr style="background: var(--table-header-bg, rgba(0,0,0,0.03)); border-bottom: 2px solid var(--panel-border);">
          <th style="padding: 10px 8px; text-align: center; width: 45px; white-space: nowrap;">#</th>
          <th style="padding: 10px 12px; text-align: left; min-width: 240px;">รายการอุปกรณ์ / ล็อต / ตำแหน่ง</th>
          <th style="padding: 10px 8px; text-align: right; width: 85px; white-space: nowrap;">ยืมไป</th>
          <th style="padding: 10px 8px; text-align: right; width: 90px; white-space: nowrap;">ค้างคืน</th>
          <th style="padding: 10px 8px; text-align: center; width: 155px; white-space: nowrap; color: var(--success, #10b981);">คืนเข้าคลัง</th>
          <th style="padding: 10px 8px; text-align: center; width: 155px; white-space: nowrap; color: var(--text-muted);">ใช้หมดไป</th>
        </tr>
      </thead>
      <tbody>
  `;

  activeBorrows.forEach((borrowTx, idx) => {
    const unreturnedQty = getBorrowUnreturnedQty(borrowTx);
    const invItem = inventory.find(i => i.id === borrowTx.itemId);
    const unit = invItem?.unit || borrowTx.unit || 'ชิ้น';
    const brandSize = [invItem?.brand, invItem?.size].filter(Boolean).join(' / ');
    const brandSizeHtml = brandSize ? `<div style="font-size: 0.8rem; color: var(--text-muted); margin-top: 1px;">(${escapeHtml(brandSize)})</div>` : '';
    const lotLoc = [borrowTx.lot || invItem?.lot || '-', invItem?.location || '-'].join(' | ');

    tableHtml += `
      <tr class="return-item-row" data-borrow-tx-id="${borrowTx.id}" data-item-id="${borrowTx.itemId}" data-max-unreturned="${unreturnedQty}" style="border-bottom: 1px solid var(--panel-border);">
        <td style="padding: 10px 8px; text-align: center; color: var(--text-muted); font-size: 0.8rem; white-space: nowrap;">${idx + 1}</td>
        <td style="padding: 10px 12px; text-align: left; min-width: 240px; word-break: break-word; line-height: 1.4;">
          <strong style="color: var(--text-main); font-size: 0.9rem; display: block;">${escapeHtml(borrowTx.itemName || invItem?.name || 'สินค้า')}</strong>
          ${brandSizeHtml}
          <div style="font-size: 0.775rem; color: var(--text-muted); margin-top: 2px;">ล็อต/ที่เก็บ: ${escapeHtml(lotLoc)}</div>
        </td>
        <td style="padding: 10px 8px; text-align: right; font-weight: 600; color: var(--text-muted); white-space: nowrap;">${borrowTx.quantity} ${escapeHtml(unit)}</td>
        <td style="padding: 10px 8px; text-align: right; font-weight: 700; color: var(--warning, #f59e0b); white-space: nowrap;">${unreturnedQty} ${escapeHtml(unit)}</td>
        <td style="padding: 10px 8px; text-align: center; white-space: nowrap;">
          <div style="display: flex; align-items: center; justify-content: center; gap: 6px;">
            <input type="number" step="any" min="0" max="${unreturnedQty}" class="input-return-qty" value="${unreturnedQty}" required style="width: 85px; min-width: 75px; padding: 6px 8px; font-size: 0.9rem; font-weight: 600; border: 1px solid var(--input-border); border-radius: 6px; background: var(--input-bg); color: var(--text-main); text-align: center; font-family: inherit; height: 38px; box-sizing: border-box;">
            <span style="font-size: 0.8rem; color: var(--text-muted); min-width: 32px; text-align: left;">${escapeHtml(unit)}</span>
          </div>
        </td>
        <td style="padding: 10px 8px; text-align: center; white-space: nowrap;">
          <div style="display: flex; align-items: center; justify-content: center; gap: 6px;">
            <input type="number" step="any" min="0" max="${unreturnedQty}" class="input-consumed-qty" value="0" required style="width: 85px; min-width: 75px; padding: 6px 8px; font-size: 0.9rem; font-weight: 600; border: 1px solid var(--input-border); border-radius: 6px; background: var(--input-bg); color: var(--text-main); text-align: center; font-family: inherit; height: 38px; box-sizing: border-box;">
            <span style="font-size: 0.8rem; color: var(--text-muted); min-width: 32px; text-align: left;">${escapeHtml(unit)}</span>
          </div>
        </td>
      </tr>
    `;
  });

  tableHtml += `
      </tbody>
    </table>
  `;

  tableWrapper.innerHTML = tableHtml;
  container.appendChild(tableWrapper);

  // Attach clamping listeners to return inputs
  tableWrapper.querySelectorAll('.return-item-row').forEach(row => {
    const max = parseFloat(row.dataset.maxUnreturned) || 0;
    const inputRet = row.querySelector('.input-return-qty');
    const inputCon = row.querySelector('.input-consumed-qty');

    const clamp = () => {
      let rVal = parseFloat(inputRet?.value);
      let cVal = parseFloat(inputCon?.value);
      if (isNaN(rVal) || rVal < 0) rVal = 0;
      if (isNaN(cVal) || cVal < 0) cVal = 0;
      if (rVal > max) {
        rVal = max;
        if (inputRet) inputRet.value = rVal;
      }
      if (rVal + cVal > max) {
        cVal = Math.max(0, Number((max - rVal).toFixed(5)));
        if (inputCon) inputCon.value = cVal;
      }
    };

    inputRet?.addEventListener('input', clamp);
    inputCon?.addEventListener('input', clamp);
  });

  // Attach toolbar buttons
  const btnFillAll = document.getElementById('btnReturnFillAll');
  if (btnFillAll) {
    btnFillAll.onclick = () => {
      tableWrapper.querySelectorAll('.return-item-row').forEach(row => {
        const max = parseFloat(row.dataset.maxUnreturned) || 0;
        const inputRet = row.querySelector('.input-return-qty');
        const inputCon = row.querySelector('.input-consumed-qty');
        if (inputRet) inputRet.value = max;
        if (inputCon) inputCon.value = 0;
      });
    };
  }

  const btnClearAll = document.getElementById('btnReturnClearAll');
  if (btnClearAll) {
    btnClearAll.onclick = () => {
      tableWrapper.querySelectorAll('.return-item-row').forEach(row => {
        const inputRet = row.querySelector('.input-return-qty');
        const inputCon = row.querySelector('.input-consumed-qty');
        if (inputRet) inputRet.value = 0;
        if (inputCon) inputCon.value = 0;
      });
    };
  }
}

/**
 * Initializes return form event listeners.
 */
export function initReturnFormEvents() {
  if (els.btnOpenReturnModal) {
    els.btnOpenReturnModal.addEventListener('click', () => {
      populateReturnDropdown();
      const section = els.divReturnItemsSection || document.getElementById('divReturnItemsSection');
      const container = els.divReturnItemsContainer || document.getElementById('divReturnItemsContainer');
      if (section) section.style.display = 'none';
      if (container) container.innerHTML = '';
      if (els.formReturn) els.formReturn.reset();
      openModal('modalReturn');
    });
  }

  const selectSession = els.selReturnBorrowTx || document.getElementById('selReturnBorrowTx');
  if (selectSession) {
    selectSession.addEventListener('change', (e) => {
      renderReturnItemsForSession(e.target.value);
    });
  }

  const formReturn = els.formReturn || document.getElementById('formReturn');
  if (formReturn) {
    formReturn.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (returnInProgress) return;

      const container = els.divReturnItemsContainer || document.getElementById('divReturnItemsContainer');
      const rows = container ? container.querySelectorAll('.return-item-row') : [];
      const { transactions } = getState();

      if (rows.length === 0) {
        showCustomAlert('กรุณาเลือกใบยืมที่มีรายการค้างคืนก่อนบันทึก');
        return;
      }

      const returnsToProcess = [];
      for (const r of rows) {
        const borrowTxId = r.dataset.borrowTxId;
        const itemId = r.dataset.itemId;
        const returnQty = parseFloat(r.querySelector('.input-return-qty')?.value) || 0;
        const consumedQty = parseFloat(r.querySelector('.input-consumed-qty')?.value) || 0;
        const borrowTx = transactions.find(t => t.id === borrowTxId);
        const maxUnreturned = borrowTx ? getBorrowUnreturnedQty(borrowTx) : 0;

        if (returnQty < 0 || consumedQty < 0) {
          showCustomAlert(`จำนวนคืนหรือจำนวนที่ใช้ไปต้องไม่ติดลบสำหรับ "${borrowTx?.itemName || 'สินค้า'}"`);
          return;
        }

        if (returnQty + consumedQty > maxUnreturned + 0.0001) {
          showCustomAlert(`จำนวนคืนรวมกับที่ใช้หมดไปของ "${borrowTx?.itemName || 'สินค้า'}" (${returnQty + consumedQty}) มากกว่าจำนวนที่ค้างคืน (${maxUnreturned})`);
          return;
        }

        if (returnQty > 0 || consumedQty > 0) {
          returnsToProcess.push({ borrowTxId, itemId, returnQty, consumedQty, borrowTx });
        }
      }

      if (returnsToProcess.length === 0) {
        showCustomAlert('กรุณาระบุจำนวนสินค้าที่ต้องการคืนหรือใช้งานหมดไปอย่างน้อย 1 รายการ');
        return;
      }

      const btnSubmit = formReturn.querySelector('button[type="submit"]');
      if (btnSubmit) {
        btnSubmit.disabled = true;
        btnSubmit.textContent = 'กำลังทำรายการ...';
      }
      returnInProgress = true;

      const currentOperator = getAuthenticatedOperatorName() || getAuthenticatedOperatorEmail() || '';
      const currentEmail = getAuthenticatedOperatorEmail() || '';

      try {
        let onlineExecuted = false;
        let onlineTimestamp = null;

        if (hasCloudSyncConfig()) {
          try {
            const rpcRes = await rpcReturnInventory(returnsToProcess.map(r => ({
              borrowTxId: r.borrowTxId,
              qtyToReturn: r.returnQty,
              consumedQty: r.consumedQty
            })), currentEmail);
            onlineExecuted = true;
            onlineTimestamp = rpcRes?.timestamp || null;
          } catch (rpcErr) {
            const errMsg = String(rpcErr?.message || '');
            if (errMsg.includes('Exceeds unreturned quantity') || errMsg.includes('เกิน')) {
              showCustomAlert(
                'ไม่สามารถทำรายการได้ เนื่องจากยอดคืนเกินจำนวนที่ค้างคืนในระบบ (อาจมีการบันทึกคืนโดยผู้ใช้อื่นแล้ว) ระบบกำลังรีเฟรชข้อมูลล่าสุด',
                'เกิดข้อผิดพลาดในการคืน'
              );
              syncAfterLocalChange('return-conflict');
              return;
            }
            console.warn('Online RPC return failed, falling back to local transaction:', rpcErr);
          }
        }

        const timestamp = onlineTimestamp || new Date().toISOString();
        const txGroupKey = `${timestamp}_return`;

        if (onlineExecuted) {
          // Server RPC has committed transactions and updated stock in Supabase.
          // Update local inventory immediately for responsive UI, without creating duplicate transactions.
          returnsToProcess.forEach(ret => {
            const { inventory } = getState();
            const invItem = inventory.find(i => i.id === ret.itemId);
            if (invItem && ret.returnQty > 0) {
              updateInventoryItem({
                ...invItem,
                quantity: Number((Number(invItem.quantity) + ret.returnQty).toFixed(5)),
                updatedAt: timestamp
              });
            }
          });
        } else {
          // Offline fallback: Update local inventory and record local return transactions
          returnsToProcess.forEach(ret => {
            const { inventory } = getState();
            const invItem = inventory.find(i => i.id === ret.itemId);
            const borrowTx = ret.borrowTx || transactions.find(t => t.id === ret.borrowTxId);
            const borrowerName = borrowTx?.borrowerName || borrowTx?.borrower_name || currentOperator;

            if (invItem && ret.returnQty > 0) {
              updateInventoryItem({
                ...invItem,
                quantity: Number((Number(invItem.quantity) + ret.returnQty).toFixed(5)),
                updatedAt: timestamp
              });
            }

            addTransaction({
              id: `tx_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
              type: 'return',
              itemId: ret.itemId,
              itemName: borrowTx?.itemName || invItem?.name || 'สินค้า',
              borrowerName: borrowerName,
              borrower_name: borrowerName,
              operatorEmail: currentEmail,
              quantity: ret.returnQty,
              consumedQty: ret.consumedQty,
              notes: `[ยืม: ${ret.borrowTxId}] คืนสินค้า (คืน ${ret.returnQty} / ใช้ไป ${ret.consumedQty} ${borrowTx?.unit || invItem?.unit || 'ชิ้น'})`,
              timestamp,
              groupKey: txGroupKey,
              updatedAt: timestamp
            });
          });
        }

        closeModal('modalReturn');
        await syncAfterLocalChange('return-transaction');
        showCustomAlert('บันทึกการคืนสินค้าเรียบร้อยแล้ว');
      } catch (err) {
        console.error('Error processing return:', err);
        showCustomAlert(`เกิดข้อผิดพลาดในการบันทึกการคืน: ${err.message}`);
      } finally {
        returnInProgress = false;
        if (btnSubmit) {
          btnSubmit.disabled = false;
          btnSubmit.textContent = 'ยืนยันการคืนสินค้า';
        }
      }
    });
  }
}

